// The entity counters of an environment (spec/api-conventions.md section 21.7; M7 slice 7): `GET …/envs/{env}/analytics/
// entities?kind=&ids=` read in batches. Every card asks for its own id; the ids asked within one tick go out as one request
// (at most 200 ids each), so a page of cards costs one read, not one per card. The answers are kept per client, environment,
// kind, range and tester choice for a minute.
import { useEffect, useMemo, useState } from 'react';
import { ApiError, type ApiClient } from '../../api/client';
import { ops, type ResponseOf } from '../../api/operations';
import { useAuth } from '../../auth/AuthContext';
import { useGame } from '../../shell/GameContext';
import { formatInstantWithZone, useTimeZone } from '../../ui/time';
import { useAnalyticsRange } from '../../workspace/analyticsRange';
import { isTempId } from '../../workspace/Workspace';
import { useLiveOpsEnv } from './env';

export type AnalyticsEntities = ResponseOf<typeof ops.getAnalyticsEntities>;
export type AnalyticsEntity = AnalyticsEntities['items'][number];
export type EntityKind = 'event' | 'offer' | 'offer_group' | 'slot' | 'ab_test' | 'ab_variant' | 'segment';

const MAX_IDS = 200;
const TTL_MS = 60_000;

interface Batch {
  items: Map<string, AnalyticsEntity>;
  failed: Map<string, ApiError>;
  pending: Set<string>;
  inflight: Set<string>;
  definitions: Record<string, string>;
  computedAt: string | null;
  loadedAt: number;
  timer: ReturnType<typeof setTimeout> | null;
  listeners: Set<() => void>;
}

let stores = new WeakMap<ApiClient, Map<string, Batch>>();

function batchOf(client: ApiClient, key: string): Batch {
  let map = stores.get(client);
  if (!map) stores.set(client, (map = new Map()));
  let batch = map.get(key);
  if (!batch) map.set(key, (batch = { items: new Map(), failed: new Map(), pending: new Set(), inflight: new Set(), definitions: {}, computedAt: null, loadedAt: 0, timer: null, listeners: new Set() }));
  return batch;
}

function notify(batch: Batch): void {
  for (const l of Array.from(batch.listeners)) l();
}

function request(batch: Batch, ids: string[], load: (ids: string[]) => Promise<AnalyticsEntities>): void {
  if (batch.loadedAt && Date.now() - batch.loadedAt > TTL_MS && batch.inflight.size === 0) {
    batch.items.clear();
    batch.failed.clear();
    batch.loadedAt = 0;
  }
  for (const id of ids) if (!batch.items.has(id) && !batch.failed.has(id) && !batch.inflight.has(id)) batch.pending.add(id);
  if (batch.pending.size === 0 || batch.timer) return;
  batch.timer = setTimeout(() => {
    batch.timer = null;
    const all = Array.from(batch.pending);
    batch.pending.clear();
    for (let i = 0; i < all.length; i += MAX_IDS) {
      const chunk = all.slice(i, i + MAX_IDS);
      for (const id of chunk) batch.inflight.add(id);
      load(chunk)
        .then((r) => {
          for (const item of r.items) batch.items.set(item.id, item);
          batch.definitions = r.definitions ?? {};
          batch.computedAt = r.computedAt ?? null;
          if (!batch.loadedAt) batch.loadedAt = Date.now();
        })
        .catch((e: unknown) => {
          const err = e instanceof ApiError ? e : new ApiError(0, 'unavailable', String(e), '');
          for (const id of chunk) batch.failed.set(id, err);
        })
        .finally(() => {
          for (const id of chunk) batch.inflight.delete(id);
          notify(batch);
        });
    }
  }, 0);
}

export interface EntityFigures {
  get: (id: string) => AnalyticsEntity | undefined;
  error: ApiError | null;
  loading: boolean;
  definitions: Record<string, string>;
  computedAt: string | null;
  from: string;
  to: string;
}

/**
 * The counters of `ids` of one kind in the selected environment over the shared range (or `range` when given: the
 * calendar's window). Unsaved documents (temp ids) are never asked.
 */
export function useEntityFigures(kind: EntityKind, ids: string[], range?: { from: string; to: string }): EntityFigures {
  const { client } = useAuth();
  const { org, game } = useGame();
  const { env } = useLiveOpsEnv();
  const shared = useAnalyticsRange(env).range;
  const from = range?.from ?? shared.from;
  const to = range?.to ?? shared.to;
  const testers = shared.includeTesters;
  const slug = env?.slug ?? null;
  const wanted = useMemo(() => ids.filter((id) => id && !isTempId(id)), [ids.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps
  const key = slug ? `${org}|${game.slug}|${slug}|${kind}|${from}|${to}|${testers}` : null;
  const batch = key ? batchOf(client, key) : null;
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!batch) return;
    const l = () => setTick((t) => t + 1);
    batch.listeners.add(l);
    return () => void batch.listeners.delete(l);
  }, [batch]);
  useEffect(() => {
    if (!batch || !slug || wanted.length === 0) return;
    request(batch, wanted, (chunk) => client.data(ops.getAnalyticsEntities, { path: { org, game: game.slug, env: slug }, query: { kind, ids: chunk.join(','), from, to, includeTesters: testers ? 'true' : null } }));
  }, [batch, wanted, client, org, game.slug, slug, kind, from, to, testers]);
  const error = batch ? (wanted.map((id) => batch.failed.get(id)).find((e) => !!e) ?? null) : null;
  return {
    get: (id) => batch?.items.get(id),
    error,
    loading: !!batch && wanted.some((id) => !batch.items.has(id) && !batch.failed.has(id)),
    definitions: batch?.definitions ?? {},
    computedAt: batch?.computedAt ?? null,
    from,
    to,
  };
}

/** Tests: drop every cached batch. */
export function resetEntityFigures(): void {
  stores = new WeakMap();
}

/** The definitions of `keys` as tooltip lines (Q-J-12), with "computed <time>" last. */
export function useDefinitionText(): (definitions: Record<string, string> | null | undefined, keys: string[], computedAt?: string | null, extra?: string[]) => string {
  const { offsets } = useGame();
  const [zone] = useTimeZone();
  return (definitions, keys, computedAt, extra = []) => {
    const lines = [...extra];
    for (const k of keys) {
      const text = definitions?.[k];
      if (text) lines.push(`${k}: ${text}`);
    }
    lines.push(computedAt ? `computed ${formatInstantWithZone(computedAt, zone, { offsets })}` : 'not computed yet');
    return lines.join('\n');
  };
}

/** `computed <time>` of a figure, or "not computed yet" (the fold has not run: computedAt null). */
export function useComputedText(): (computedAt: string | null | undefined) => string {
  const { offsets } = useGame();
  const [zone] = useTimeZone();
  return (computedAt) => (computedAt ? `computed ${formatInstantWithZone(computedAt, zone, { offsets })}` : 'not computed yet');
}

/** A count from the wire (`number | string`). */
export function n(value: number | string | null | undefined): number {
  return value === null || value === undefined ? 0 : Number(value);
}
